// src/pages/Auth/OTPVerification.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { verifyOtpThunk, requestOtpThunk } from "../../redux/features/Auth/authThunk";
import { Loader2, Key, ArrowLeft } from "lucide-react";

const OTPVerification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const phoneFromState = useSelector((state) => state.auth.phone);
  const { loading, error, isNewUser, userInfo } = useSelector((state) => state.auth);
  const phone = location.state?.phone || phoneFromState;

  // code[0] = سمت چپ (اولین رقم), code[5] = سمت راست (آخرین رقم)
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(120);

  useEffect(() => {
    if (!phone) navigate("/phone");
  }, [phone, navigate]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  useEffect(() => {
    if (userInfo?.access && !isNewUser) {
      navigate("/");
    }
  }, [userInfo, isNewUser, navigate]);

  // ============================================
  // ✅ فوکوس اولیه روی راست‌ترین خانه (index 0)
  // ============================================
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // ============================================
  // ✅ ورودی: حرکت از راست → چپ
  // ============================================
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    // بعد از پر شدن، به خانه بعدی (سمت چپ) برو
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace: به خانه قبلی (سمت راست) برو
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  // ============================================
  // ✅ چسباندن (Paste) کل کد
  // ============================================
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pastedData.length === 0) return;
    
    const digits = pastedData.split("").slice(0, 6);
    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) newCode[index] = digit;
    });
    setCode(newCode);
    
    // فوکوس روی آخرین خانه‌ای که پر شده یا خانه بعدی
    const lastFilledIndex = Math.min(digits.length - 1, 5);
    if (digits.length < 6) {
      inputRefs.current[digits.length]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = () => {
    const otp = code.join("");
    if (otp.length !== 6) {
      alert("کد ۶ رقمی را کامل وارد کنید.");
      return;
    }
    dispatch(verifyOtpThunk({ phone, code: otp })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        if (res.payload.is_new_user) {
          navigate("/set-username");
        } else {
          navigate("/");
        }
      }
    });
  };

  const resendCode = () => {
    if (timer > 0) return;
    dispatch(requestOtpThunk(phone));
    setTimer(120);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-[#F0F4EB] flex items-center justify-center py-8 px-4" dir="rtl">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-[#DCE4D3]">
          <div className="relative bg-gradient-to-l from-[#8A9A7B] to-[#9EAD8C] px-6 py-8 text-white">
            <button
              onClick={() => navigate(-1)}
              className="absolute top-4 right-4 p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                <Key className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold">کد تایید</h1>
              <p className="text-white/80 text-sm mt-1">
                کد ارسال شده به <span className="font-bold">{phone}</span> را وارد کنید
              </p>
            </div>
          </div>

          <div className="p-6">
            {/* ============================================
                ✅ شش کادر ورود کد با `flex-direction: row-reverse`
                ============================================ */}
            <div 
              className="flex justify-center gap-2 my-6" 
              style={{ display: 'flex', flexDirection: 'row-reverse' }}
              onPaste={handlePaste}
            >
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={code[index]}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#8A9A7B] focus:ring-2 focus:ring-[#8A9A7B]/30 outline-none transition-all"
                  style={{ textAlign: "center" }}
                  autoComplete="off"
                />
              ))}
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center mb-4">{error}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-3.5 bg-[#8A9A7B] text-white rounded-xl font-bold hover:bg-[#6A7A5B] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  در حال تایید...
                </>
              ) : (
                "تایید کد"
              )}
            </button>

            <div className="text-center mt-4">
              <button
                onClick={resendCode}
                disabled={timer > 0}
                className={`text-sm font-medium ${
                  timer > 0 ? "text-gray-400 cursor-not-allowed" : "text-[#8A9A7B] hover:underline"
                }`}
              >
                {timer > 0 ? `ارسال مجدد (${formatTime(timer)})` : "ارسال مجدد کد"}
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 text-center text-xs text-gray-400">
              <p>کد تایید تا ۲ دقیقه معتبر است.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;